import React, { useEffect, useState, useCallback, useRef } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { getSpendingBreakdown } from "../../api/savingsClient";
import { formatCurrency, formatCurrencyCompact } from "../../utils/formatters";
import ChartLoader from "../ui/ChartLoader";

const COLORS = [
    "#2563EB", "#0EA5E9", "#6366F1", "#8B5CF6", "#EC4899",
    "#F97316", "#F59E0B", "#10B981", "#06B6D4", "#F43F5E",
    "#84CC16", "#A855F7",
];

function SpendingTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md text-sm">
            <p className="text-gray-900 font-semibold">{d.displayName}</p>
            <p className="text-gray-700">{formatCurrency(d.amount)}</p>
            <p className="text-gray-500 text-xs">{d.percentage}% of spending</p>
            <p className="text-gray-500 text-xs">{d.transactionCount} transactions</p>
        </div>
    );
}

export default function SpendingBreakdownCard({ months = 6, onMonthsChange, syncing = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const fetchData = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setLoading(true);
        setError(null);

        try {
            const resp = await getSpendingBreakdown(months, abortRef.current.signal);
            setData(resp);
            setLoading(false);
        } catch (e) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
            setError("Failed to load spending data");
            setLoading(false);
        }
    }, [months]);

    useEffect(() => {
        fetchData();
        return () => abortRef.current?.abort();
    }, [fetchData]);

    if (loading) {
        return (
            <div className="card p-6">
                <div className="h-5 bg-gray-100 rounded w-40 mb-4 animate-pulse" />
                <ChartLoader height={192} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6">
                <p className="text-gray-500 text-sm">{error}</p>
            </div>
        );
    }

    if (!data || data.categories.length === 0) {
        return (
            <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Spending breakdown</h3>
                    <div className="flex gap-1 text-xs">
                        {[1, 3, 6].map(m => (
                            <button
                                key={m}
                                onClick={() => onMonthsChange && onMonthsChange(m)}
                                className={`px-2 py-1 rounded font-medium ${
                                    months === m
                                        ? "bg-accent-50 text-accent-600"
                                        : "text-gray-500 hover:text-gray-900"
                                }`}
                            >
                                {m}mo
                            </button>
                        ))}
                    </div>
                </div>
                {syncing ? (
                    <div className="flex items-center gap-3 py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-gray-200 border-t-accent-500 rounded-full" />
                        <p className="text-gray-500 text-sm">Syncing your financial data...</p>
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm">No spending data available for this period.</p>
                )}
            </div>
        );
    }

    const topCategories = data.categories.slice(0, 8);
    const otherCategories = data.categories.slice(8);
    const chartCategories = [...topCategories];

    if (otherCategories.length > 0) {
        const otherAmount = otherCategories.reduce((sum, c) => sum + c.amount, 0);
        const otherPct = otherCategories.reduce((sum, c) => sum + c.percentage, 0);
        const otherCount = otherCategories.reduce((sum, c) => sum + c.transactionCount, 0);
        chartCategories.push({
            category: "OTHER",
            displayName: "Other",
            amount: otherAmount,
            percentage: otherPct,
            transactionCount: otherCount,
        });
    }

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Spending breakdown</h3>
                <div className="flex gap-1 text-xs">
                    {[1, 3, 6].map(m => (
                        <button
                            key={m}
                            onClick={() => onMonthsChange && onMonthsChange(m)}
                            className={`px-2 py-1 rounded font-medium ${
                                months === m
                                    ? "bg-accent-50 text-accent-600"
                                    : "text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            {m}mo
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-6 flex-wrap">
                <div className="w-48 h-48 flex-shrink-0" style={{ willChange: "transform" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartCategories}
                                cx="50%"
                                cy="50%"
                                innerRadius={45}
                                outerRadius={75}
                                paddingAngle={2}
                                dataKey="amount"
                            >
                                {chartCategories.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />
                                ))}
                            </Pie>
                            <Tooltip content={<SpendingTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="flex-1 space-y-2 overflow-hidden min-w-[200px]">
                    {chartCategories.map((cat, i) => (
                        <div key={cat.category} className="flex items-center gap-2 text-sm">
                            <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: COLORS[i % COLORS.length] }}
                            />
                            <span className="text-gray-700 truncate flex-1">{cat.displayName}</span>
                            <span className="text-gray-900 font-medium whitespace-nowrap">
                                {formatCurrencyCompact(cat.amount)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-500">
                    Total: {formatCurrencyCompact(data.totalSpending)} across {data.transactionCount} transactions
                </p>
            </div>
        </div>
    );
}
